
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCookie } from "cookies-next";
import { authService } from "../api/authServices";
import { AuthState } from "@/types/auth";

// ✅ Convert `getCookie` return type to `string | null`
const getToken = (): string | null => {
  const token = getCookie("adminToken");
  return typeof token === "string" ? token : null;
};

// Function to get the phone number safely
const getPhoneNumber = (): string | null => {
  const phone = getCookie("adminPhone");
  return typeof phone === "string" ? phone : null;
};

const getUserExists = (): string | null => {
  const exists = getCookie("adminExists");
  return typeof exists === "string" ? exists : null;
};

const getBulkUserID = (): string | null => {
  const userid = getCookie("adminUserID");
  return typeof userid === "string" ? userid : null;
};

const getCreditLimit = (): string | null => {
  const creditLimit = getCookie("adminCreditLimit");
  return typeof creditLimit === "string" ? creditLimit : null;
};

// Initial State
const initialState: AuthState = {
  isAuthenticated: !!getToken(),
  phoneNumber: getPhoneNumber(),
  userData: null,
  bulkUserID: getBulkUserID(),
  token: getToken(),
  userRole: null,
  firstLogin: false,
  creditLimit: getCreditLimit(),
  userExists: getUserExists(),
  loading: false,
  error: null,
};

// ✅ Send OTP API
export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (PhoneNumber: string, { rejectWithValue }) => {
    try {
      const data = await authService.sendOTP(PhoneNumber);
      return data;
    } catch (error) {
      console.error("Redux API Error:", error);
      return rejectWithValue("Network error. Please try again.");
    }
  }
);

export const fetchUserRole = createAsyncThunk(
  "auth/fetchUserRole",
  async (bulkUserID: string | null, { rejectWithValue }) => {
    try {
      const data = await authService.fetchUserRole(bulkUserID);
      return data;
    } catch (error) {
      console.error("Redux API Error:", error);
      return rejectWithValue("Network error. Please try again.");
    }
  }
);

export const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async (bulkUserID: string | null, { rejectWithValue }) => {
    try {
      const data = await authService.fetchUser(bulkUserID);
      return data?.[0];
    } catch (error) {
      console.error("Redux API Error:", error);
      return rejectWithValue("Network error. Please try again.");
    }
  }
);

// ✅ Verify OTP API
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (
    { PhoneNumber, OTP }: { PhoneNumber: string; OTP: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await authService.verifyOTP(PhoneNumber, OTP);
      return data;
    } catch (error) {
      console.error("API Error:", error);
      return rejectWithValue("Invalid OTP or Network error. Please try again.");
    }
  }
);

// ✅ Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      authService.logout();
      state.isAuthenticated = false;
      state.userData = null;
      state.userExists = null;
      state.bulkUserID = null;
      state.phoneNumber = null;
      state.token = "";
    },
    setBulkUserID: (state, action) => {
      state.bulkUserID = action.payload;
    },
    setFirstLogin: (state, action) => {
      state.firstLogin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.phoneNumber = action.payload.PhoneNumber;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserRole.fulfilled, (state, action) => {
        state.userRole = action.payload;
      })
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, setBulkUserID, setFirstLogin } = authSlice.actions;
export default authSlice.reducer;
